import React, { useEffect, useState } from "react";
import { useTypedDispatch, useTypedSelector } from "@lib/redux";
import { addNewNotification } from "@lib/redux/slices/notifications";
import WebSocketService from "@lib/services/websocket";
import { getNotificationProps } from "@lib/helpers/notificationHelper";
import { i18n } from "@lib/helpers/i18n";
import { Socket } from "socket.io-client";
import { useApi } from "@lib/hooks/APIHooks";
import { useIntl } from "react-intl";

interface WebSocketDataProviderProps {
  children: React.ReactNode;
}

const WebSocketDataProvider: React.FC<WebSocketDataProviderProps> = ({
  children,
}) => {
  const { messages } = useIntl();
  const dispatch = useTypedDispatch();
  const { role = null } = useTypedSelector((state) => state?.auth?.user) || {};
  const { error } = useTypedSelector((state) => state.websocket) || {};
  const { locale } = useTypedSelector((state) => state.settings.locale) || {};
  const [socket, setSocket] = useState<Socket | null>(null);
  const { refetchCaches } = useApi();

  // Initialize the WebSocket service and request notification permissions
  useEffect(() => {
    // Request notification permissions
    window?.Notification?.requestPermission()
      .then((permission) => {
        console.log("Notification permission:", permission);
      })
      .catch((err) => {
        console.error("Error requesting notification permission:", err);
      });

    // Initialize WebSocket service
    WebSocketService.init();

    // Cleanup function
    return () => {
      if (socket) {
        socket.off("manager-message");
      }
    };
  }, []);

  // Monitor the socket connection and update local state when socket becomes available
  useEffect(() => {
    const checkSocketInterval = setInterval(() => {
      const currentSocket = WebSocketService.getSocket();
      if (currentSocket && currentSocket !== socket) {
        setSocket(currentSocket);
      }
    }, 1000);

    return () => {
      clearInterval(checkSocketInterval);
    };
  }, [socket]);

  // Set up socket event listeners when socket becomes available
  useEffect(() => {
    if (!socket) return;

    // Set up event listeners
    const handleManagerMessage = (data: any) => {
      dispatch(addNewNotification(data));
      // console.log("🚀 ~ manager-message received ~ data:", data);
      const notificationProps = getNotificationProps({
        notification: data,
        role,
        messages,
      });

      if (notificationProps.clearKeys) {
        refetchCaches({ searchKeys: notificationProps.clearKeys });
      }

      WebSocketService.handleNotifyMessage({
        url: notificationProps.url,
        title: i18n({
          locale: locale || "en",
          id: notificationProps.title.id,
          params: notificationProps?.title?.values,
        }),
        body: i18n({
          locale: locale || "en",
          id: notificationProps.description.id,
          params: notificationProps?.description?.values,
        }),
      });
    };

    // Add event listener
    socket.on("manager-message", handleManagerMessage);

    // Cleanup function to remove listeners when component unmounts or socket changes
    return () => {
      socket.off("manager-message", handleManagerMessage);
    };
  }, [socket, locale]);

  // Handle WebSocket errors
  useEffect(() => {
    if (!!error) {
      // toast.error(error);
    }
  }, [error]);

  return <>{children}</>;
};

export default WebSocketDataProvider;
